import { useQuery } from '@apollo/client'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { GET_ORDER_STATS } from '../../graphql/queries'
import { formatEuros, dateRangeToUtc } from '../../lib/format'
import type { OrderStats } from '../../types/order'
import { MarginWaterfall } from '../../components/financials/MarginWaterfall'
import { buildSourceColumns, SourceBreakdown } from '../../components/financials/SourceBreakdown'
import { salesCents, avgOrderCents } from '../../components/financials/salesBasis'
import {
  SectionTitle,
  StatCard,
  EmptyData,
  TableLoading,
  QueryError,
} from './shared'

// ─── Financial Overview Tab ───────────────────────────────────────────────────
// Service-day view of orderStats — the same financial dashboard as DashboardPage,
// over the page's arbitrary date range. Unlike the Tax report (which counts
// on payment day, created_at), orderStats counts orders on their service day
// (scheduled_for), so totals can legitimately differ for the same range.

const FinancialOverviewTab = ({ startDate, endDate }: { startDate: string; endDate: string }) => {
  const { data, loading, error, refetch } = useQuery<{ orderStats: OrderStats }>(GET_ORDER_STATS, {
    variables: { filters: dateRangeToUtc(startDate, endDate) },
    fetchPolicy: 'cache-and-network',
    skip: !startDate || !endDate,
  })

  const stats = data?.orderStats

  const serviceDayNote = (
    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-700">
      This overview counts orders on their <span className="font-semibold">service day</span> (the
      day the food is prepared). The Tax report counts on payment day, so totals can
      legitimately differ for the same range.
    </div>
  )

  if (loading && !stats) return <div className="space-y-6">{serviceDayNote}<TableLoading /></div>
  if (error) return <div className="space-y-6">{serviceDayNote}<QueryError message={`Failed to load financial overview: ${error.message}`} /></div>
  if (!stats) return null

  // This tab is behind order.financial.view, so every figure is on the ex-VAT basis.
  const { columns, total } = buildSourceColumns(stats, true)

  return (
    <div className="space-y-6">
      {serviceDayNote}

      {/* Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Goods plus both fees, before discounts — the incl-VAT twin of Turnover. */}
        <StatCard
          label="Gross Revenue (incl. VAT)"
          value={formatEuros(salesCents(stats, false))}
          sub={(stats.totalDiscountCents ?? 0) > 0 ? `−${formatEuros(stats.totalDiscountCents ?? 0)} discounts` : undefined}
        />
        <StatCard
          label="Turnover (excl. VAT)"
          value={formatEuros(stats.turnoverExVatCents ?? 0)}
          sub={`VAT ${formatEuros(stats.outputVatCents ?? 0)}`}
        />
        <StatCard
          label="Product Margin"
          value={formatEuros(stats.contributionMarginCents ?? 0)}
          sub={stats.contributionMarginPct != null ? `${stats.contributionMarginPct.toFixed(1)}% of product sales` : undefined}
        />
        <StatCard
          label="Orders"
          value={String(stats.orderCount ?? 0)}
          sub={`avg ${formatEuros(avgOrderCents(stats, true))} ex. VAT, without delivery fee`}
        />
      </div>

      {(stats.orderCount ?? 0) === 0 ? (
        <EmptyData />
      ) : (
        <>
          {/* Sales by channel — one column per channel the order was placed on */}
          <div>
            <div className="flex items-center justify-between">
              <SectionTitle>Sales by Channel</SectionTitle>
              {(stats.linesWithoutCogsCount ?? 0) > 0 && (
                <p className="text-xs text-amber-600 mb-4">
                  {stats.linesWithoutCogsCount} items missing recipe — COGS incomplete, actual margin lower than shown
                </p>
              )}
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <SourceBreakdown columns={columns} total={total} canFin={true} />
            </div>
          </div>

          {/* Staff meals (comps — excluded from the figures above) */}
          {(stats.staffMealCount ?? 0) > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 px-5 py-4 flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Staff Meals</p>
                <p className="text-xs text-gray-400 mt-0.5">Comps booked at €0 — excluded from sales, discounts &amp; product margin above.</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-2xl font-bold text-gray-900 leading-none">{formatEuros(stats.staffMealCostCents ?? 0)}</p>
                <p className="text-xs text-gray-400 mt-0.5">cost absorbed · {stats.staffMealCount ?? 0} meals</p>
              </div>
            </div>
          )}

          {/* Money flow */}
          <div>
            <SectionTitle>Money Flow (total)</SectionTitle>
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <MarginWaterfall stats={stats} />
            </div>
          </div>
        </>
      )}

      <div className="flex justify-end">
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
    </div>
  )
}

export default FinancialOverviewTab
